import React, { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { CareerJourneyTimeline } from './MindQPlatformViews';
import { ArrowRight, CheckCircle, X } from 'lucide-react';

const classRoadmaps = {
  'Class 7': {
    title: 'Early Self Discovery',
    intro: 'The child starts with simple, age-appropriate discovery around interests, learning style, habits, confidence, and curiosity.',
    steps: ['Parent/student intake', 'Interest and learning style screening', 'Study habit review', 'Strength summary', 'Parent guidance note'],
    outcome: 'A clear foundation for how the child learns best and what areas need encouragement before career pressure begins.',
  },
  'Class 8': {
    title: 'Strength Mapping',
    intro: 'The child begins mapping strengths through aptitude basics, multiple intelligence indicators, emotional awareness, and study direction.',
    steps: ['Aptitude basics', 'Multiple intelligence profile', 'Subject comfort check', 'Learning gaps review', 'Growth roadmap'],
    outcome: 'Early direction for subject comfort, skill growth, and future stream possibilities without forcing a decision too early.',
  },
  'Class 9': {
    title: 'Career Awareness',
    intro: 'The student explores career clusters, subject fit, learning preferences, and future skills before the Class 10 stream decision.',
    steps: ['Career exposure assessment', 'Subject preference mapping', 'Future skills checklist', 'Career cluster shortlist', 'Class 10 readiness plan'],
    outcome: 'A practical awareness roadmap connecting subjects with careers like technology, design, medical, commerce, law, and civil services.',
  },
  'Class 10': {
    title: 'Stream Selection',
    intro: 'The student takes a comprehensive assessment and receives guidance for Science, Commerce, Humanities, vocational, and emerging pathways.',
    steps: ['Comprehensive assessment', 'Aptitude and interest fit', 'Stream comparison', 'Subject combination guidance', 'Parent-student counseling'],
    outcome: 'A stream recommendation with reasoning, fit areas, risks, and next steps for Class 11 subject selection.',
  },
  'Class 11': {
    title: 'Career Planning',
    intro: 'The student validates the selected stream and builds a career roadmap with entrance exam direction, skill priorities, and college pathways.',
    steps: ['Career cluster validation', 'Entrance exam direction', 'Skill gap plan', 'College pathway shortlist', 'Monthly guidance checkpoints'],
    outcome: 'A focused plan for careers such as engineering, medical, law, design, commerce, management, AI, entrepreneurship, or civil services.',
  },
  'Class 12': {
    title: 'College Applications',
    intro: 'The student converts stream and career clarity into course selection, college shortlisting, applications, and transition planning.',
    steps: ['Final career fit review', 'Course and college shortlist', 'Application timeline', 'Counselor review', 'Career success transition plan'],
    outcome: 'A college-readiness roadmap with admission actions, backup options, and final career fit review.',
  },
  'Career Success': {
    title: 'Higher Education and Guided Growth',
    intro: 'After school, MindVrik guidance supports higher education decisions, skill building, and confidence for the next stage.',
    steps: ['Course decision review', 'Skill-building plan', 'Mentor guidance', 'Progress check-ins', 'Career confidence plan'],
    outcome: 'Continued direction so the student moves from school choices into higher education and career growth with clarity.',
  },
};

const RoadmapModal = ({ selected, onClose, onStartAssessment }) => {
  if (!selected) return null;
  const details = classRoadmaps[selected.grade] || {};

  return (
    <div className="platform-overlay" role="dialog" aria-modal="true" aria-label={`${selected.grade} roadmap`}>
      <div className="platform-panel platform-panel-wide">
        <button className="platform-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        <div className="section-label">Student Roadmap</div>
        <h2 className="platform-title">{selected.grade}: {details.title || selected.goal}</h2>
        <p className="platform-lead">{details.intro}</p>

        <div className="grade-roadmap-steps">
          {(details.steps || []).map((step, index) => (
            <div className="journey-node" key={step}>
              <strong>Step {index + 1}</strong>
              <span>{step}</span>
            </div>
          ))}
        </div>

        <div className="platform-summary">
          <h3>Final outcome</h3>
          <div className="platform-check"><CheckCircle size={14} />{details.outcome}</div>
          <div className="platform-check"><CheckCircle size={14} />Assessment report + expert counseling creates the personalized stream/career roadmap.</div>
        </div>

        <div className="grade-roadmap-actions">
          <button className="btn-primary" onClick={onStartAssessment}>Start Assessment <ArrowRight size={15} /></button>
          <button className="btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const CareerJourney = ({ onOpenAssessment }) => {
  const [ref, visible] = useScrollReveal();
  const [selectedStep, setSelectedStep] = useState(null);

  return (
    <section
      ref={ref}
      style={{ padding: '96px 24px', background: '#F9F9F7' }}
      data-testid="career-journey-section"
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div className={`reveal ${visible ? 'visible' : ''}`} style={{ textAlign: 'center', marginBottom: '48px' }}>
          <div className="section-label">Career Journey Timeline</div>
          <div className="section-divider" />
          <h2 style={{
            fontFamily: 'Manrope', fontWeight: 700, fontSize: 'clamp(30px,3.8vw,46px)',
            color: '#1A202C', margin: '20px 0 16px', letterSpacing: '-0.02em',
          }}>
            Guidance from <span style={{ color: '#2A9D8F' }}>Class 7 to Success</span>
          </h2>
          <p style={{ fontSize: '16px', color: '#64748B', maxWidth: '560px', margin: '0 auto', lineHeight: 1.7 }}>
            A calm roadmap for self discovery, awareness, stream selection, college planning, and confident career growth.
          </p>
        </div>
        <div className={`reveal reveal-delay-2 ${visible ? 'visible' : ''}`}>
          <CareerJourneyTimeline onSelectStep={setSelectedStep} />
        </div>
      </div>
      <RoadmapModal
        selected={selectedStep}
        onClose={() => setSelectedStep(null)}
        onStartAssessment={() => {
          setSelectedStep(null);
          onOpenAssessment?.();
        }}
      />
    </section>
  );
};

export default CareerJourney;
